/**
 * Brand naming, decoupled from any UI component so it can be imported wherever a
 * brand label is needed (i18n config, hooks, plain modules) without pulling in
 * logo/SVG assets or the feature-flags client. This file is runtime-dependency
 * free; consume it via `@brightdrop/bd-ui/brand/constants` from plain modules
 * (configs, models) or via `@brightdrop/bd-ui/brand` alongside the React hook.
 */

import type { i18n as I18nInstance } from 'i18next';

export const ONSTAR_FLEET_INTELLIGENCE_REBRAND_FLAG =
  'SDPCPLT-7215-onstar-fleet-intelligence-rebrand';

/**
 * Name of the i18next interpolation variable that carries the brand label
 * (`{{brandName}}` in copy). Single-sourced here so the config seed, the sync
 * writer, and any reader reference the same key instead of a magic string.
 */
export const BRAND_INTERPOLATION_VAR = 'brandName';

/**
 * Current active brand label, rendered in user-facing copy when the rebrand
 * flag is on. Keep the brand as a single variable so a future rebrand is a
 * one-line value change (today's BRAND_NAME drops into LEGACY_BRAND_NAME and
 * BRAND_NAME gets the new value).
 */
export const BRAND_NAME = 'OnStar Fleet Intelligence';

/** Previous brand label, retained for flag-off / legacy surfaces. */
export const LEGACY_BRAND_NAME = 'GM Fleet';

/**
 * Resolves the brand label shown in UI copy; flipped via the rebrand flag.
 * @param isRebrandEnabled - When true, returns the current brand; otherwise legacy.
 */
export const getBrandName = (isRebrandEnabled: boolean): string =>
  isRebrandEnabled ? BRAND_NAME : LEGACY_BRAND_NAME;

/**
 * Reads the current brand label off an i18next instance's interpolation
 * defaults (kept up to date by BrandI18nSync). Use this where the rebrand flag
 * isn't directly reachable (e.g. a federated remote relying on the host's synced
 * i18next), falling back to the legacy label when nothing has been seeded.
 * @param instance - The i18next instance to read the brand from.
 */
export const getBrandNameFromI18n = (instance: I18nInstance): string =>
  (instance.options.interpolation?.defaultVariables?.[
    BRAND_INTERPOLATION_VAR
  ] as string | undefined) ?? LEGACY_BRAND_NAME;
